/**
 * Workforce view — MineTwin user roster.
 *
 * Phase 3 of the Workforce Alignment Plan: this is the unified
 * person-management surface. The old separate Workforce / Members split is
 * gone; identity (`user`) and workforce attributes (`occupation`) live on
 * the same row. The permission tier (`app_role`) is JWT-driven and reflected
 * onto `user.app_role` on every connect — not editable here.
 *
 * Reducer surface (all renamed from Phase 2):
 *   - add_user(email, occupation, project_id) — creates user OR stages
 *     pending_user that drains on first sign-in.
 *   - set_user_occupation(user_id, occupation) — admin-edits the mine job.
 *   - archive_user(user_id) — soft-detach from every active site
 *     (user_site.active = false, left_at = now). Keeps the user row.
 *   - delete_user(user_id) — hard delete + cascade user_site. Refuses if
 *     active assignments exist.
 *
 * The Phase 2 "Set role" drawer and the Phase 1/2 "Promote me to
 * Coordinator" button are GONE. Both were workarounds for the retired
 * Writer-bypass / project_member.member_role model. OrgAdmin tier
 * short-circuits every per-site auth check; no self-promote needed.
 */

import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '@/components/Icon';
import { PortalIcons } from '@/components/portalIcons';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSpacetimeDB, useTable } from 'spacetimedb/react';
import { tables as minetwinTables } from '@sdk/minetwin';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { useUsers, type UserRow } from '@/hooks/useUsers';
import { useProjects } from '@/hooks/useProjects';
import { useTweaks } from '@/hooks/useTweaks';
import { useRunReducer } from '@/hooks/useRunReducer';
import { useWorkosRoster, type WorkosRosterState } from '@/hooks/useWorkosRoster';
import { useMySiteMembership } from '@/hooks/useMySiteMembership';
import { useOccupations } from '@/hooks/useOccupations';
import { useAuthKit } from '@/contexts/AuthProvider';
import { sendWorkosInvite } from '@/services/workosInvite';
import { OccupationSelect } from '@/components/OccupationSelect';
import { NoProjects } from '@/components/NoProjects';
import { DrawerShell } from '@/components/Workforce/DrawerShell';
import { AddUserModal } from '@/components/Workforce/AddUserModal';
import { BulkAddUsersModal } from '@/components/Workforce/BulkAddUsersModal';
import { useAddSiteUser, type AddSiteUserInput } from '@/hooks/useAddSiteUser';
import { useIsOrgAdmin } from '@/hooks/useIsOrgAdmin';
import { useSiteAccess } from '@/hooks/useSiteAccess';
import { useRole } from '@/hooks/useRole';
import { ALLOW_AUTHORING_HINT, showAllowAuthoring, showOrientationAdminSwitch } from '@/lib/siteAccess';
import { setWorkosMemberRole } from '@/services/workosMemberRole';
import type { RowOutcome } from '@/lib/bulkAddUsers';
import type { Site, Toast } from '@/data/types';
import { GatedButton } from '@/components/GatedButton';

type WorkforceProps = { toast: (t: Omit<Toast, 'id'>) => void };

type ModalState =
  | { kind: 'none' }
  | { kind: 'add' }
  | { kind: 'bulk' }
  | { kind: 'occupation'; user: UserRow };

export function Workforce({ toast }: WorkforceProps) {
  const dialogs = useDialogs();
  const minetwinState = useSpacetimeDB('minetwin');
  const { users, isReady } = useUsers();
  const { sites, isReady: sitesReady } = useProjects();
  const { tweaks } = useTweaks();
  const { organizationId, getAccessToken } = useAuthKit();
  // WorkOS roster cross-check — distinguishes "pending portal sign-in"
  // (real WorkOS member, just hasn't connected to the MineTwin module
  // yet) from "invite outstanding" from "neither". Powers the
  // StatusPill rendered for each row.
  const workosRoster = useWorkosRoster();
  // Pre-warm the Databricks occupation cache while the coordinator is
  // scanning the roster — by the time they click "+ Add user" the
  // OccupationSelect dropdown reads from the cache instead of waiting
  // through the SQL Warehouse cold start. Discard the return value;
  // OccupationSelect calls `useOccupations` itself but shares the same
  // module-level cache, so the prefetch and the in-drawer call dedup
  // to a single round-trip.
  useOccupations();
  // Current site drives the default projectId for new-user writes. If the
  // org subscription hasn't delivered any projects yet, the form will require
  // a manual projectId entry.
  const currentSite: Site | undefined = sites[tweaks.siteIndex] ?? sites[0];
  const defaultProjectId = currentSite?.projectName ?? '';
  // #146: unlike the other per-site pages, Workforce renders a BANNER
  // instead of the full-page NoSiteMembership gate — this page is where
  // membership gets fixed, so blocking it would strand the admin the
  // gate's own copy sends here.
  const membership = useMySiteMembership(defaultProjectId);

  const [modal, setModal] = useState<ModalState>({ kind: 'none' });
  // Email currently mid-"Resend invite" — gates that row's action against
  // double-fire. Separate from `busy` (reducer-only).
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  // Single Add user in flight. Separate from `busy` because the add
  // pipeline awaits three services, not one reducer.
  const [adding, setAdding] = useState(false);
  const { runReducer, busy } = useRunReducer(toast);

  // The three-write add pipeline (minetwin add_user → org grant → WorkOS
  // invite), shared with the Members page.
  const { addOneUser, inviteEnabled } = useAddSiteUser(currentSite);
  // Only an org admin may grant site Admin (#241).
  const viewerIsOrgAdmin = useIsOrgAdmin();
  // ORIENTATION-ROLES P1/P3/D9: invite and the site role picker for the
  // site's Admin or the org admin; Allow authoring for any site admin; the
  // Orientation admin switch for WorkOS admins (the API checks it again).
  const access = useSiteAccess(defaultProjectId);
  const viewerIsWorkosAdmin = useRole() === 'admin';
  const [rightRows] = useTable('minetwin', minetwinTables.visible_site_rights);
  const rightsByEmail = useMemo(() => {
    const m = new Map<string, { role: string; allowAuthoring: boolean }>();
    for (const r of rightRows) {
      if (r.projectId === defaultProjectId) m.set(r.email, r);
    }
    return m;
  }, [rightRows, defaultProjectId]);
  // The module reads the WorkOS role on the next connection, so app_role
  // lags a switch flip; remember what this session set.
  const [orientationAdminSet, setOrientationAdminSet] = useState<Map<string, boolean>>(new Map());

  const connOnline = minetwinState.isActive;
  // Roster is scoped to the current site only — the cross-site "All sites"
  // toggle was retired because workforce is a per-site concern. If no site
  // is selected (transient load window), show everything the user can see.
  const visibleUsers = useMemo(() => {
    if (!currentSite) return users;
    return users.filter((u) => u.projectIds.includes(currentSite.projectName));
  }, [users, currentSite]);
  const boundCount = visibleUsers.filter((u) => u.bound).length;
  const pendingCount = visibleUsers.length - boundCount;

  // --- Smart-table state ---------------------------------------------------
  // Free-text search across email + display name + occupation. Substring,
  // case-insensitive.
  const [search, setSearch] = useState('');
  // Tri-state status filter (signed-in vs pending vs everyone).
  const [statusFilter, setStatusFilter] =
    useState<'all' | 'bound' | 'pending'>('all');
  // Single-pick tier filter — matches `user.app_role.tag`. 'all' bypasses
  // the filter. Pending users (no app_role yet) are dropped from the
  // result when a specific tier is picked.
  const [tierFilter, setTierFilter] = useState<string>('all');
  // Click a column header to sort by it. Click the same column again to
  // flip direction. New picks default to ascending.
  type SortCol = 'email' | 'occupation' | 'tier' | 'status';
  const [sortBy, setSortBy] = useState<{ col: SortCol; dir: 'asc' | 'desc' }>(
    { col: 'email', dir: 'asc' },
  );

  const filteredAndSortedUsers = useMemo(() => {
    let rows = visibleUsers;
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (u) =>
          u.email.toLowerCase().includes(q) ||
          u.displayName.toLowerCase().includes(q) ||
          u.occupation.toLowerCase().includes(q),
      );
    }
    if (statusFilter === 'bound') rows = rows.filter((u) => u.bound);
    else if (statusFilter === 'pending') rows = rows.filter((u) => !u.bound);
    if (tierFilter !== 'all') {
      rows = rows.filter((u) => u.bound && u.appRole?.tag === tierFilter);
    }
    // Stable sort via a copy; .sort is in-place.
    const dir = sortBy.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let av = '';
      let bv = '';
      switch (sortBy.col) {
        case 'email':
          av = a.email;
          bv = b.email;
          break;
        case 'occupation':
          av = a.occupation;
          bv = b.occupation;
          break;
        case 'tier':
          // Sort bound users by tier tag; pending users fall to the end
          // so the active roster reads first when sorting by tier.
          av = a.bound ? (a.appRole?.tag ?? '') : '~pending';
          bv = b.bound ? (b.appRole?.tag ?? '') : '~pending';
          break;
        case 'status':
          // Bound users first (asc), pending last; flips with dir.
          av = a.bound ? '0' : '1';
          bv = b.bound ? '0' : '1';
          break;
      }
      return av.localeCompare(bv) * dir;
    });
  }, [visibleUsers, search, statusFilter, tierFilter, sortBy]);

  function toggleSort(col: SortCol) {
    setSortBy((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'asc' },
    );
  }

  async function handleAdd(input: AddSiteUserInput) {
    if (adding) return;
    setAdding(true);
    let outcome: RowOutcome;
    try {
      outcome = await addOneUser(input);
    } finally {
      setAdding(false);
    }
    setModal({ kind: 'none' });
    if (outcome.kind === 'failed') {
      toast({ title: 'Add user failed', sub: outcome.note, kind: 'bad' });
      return;
    }
    // Refresh the WorkOS roster so the StatusPill on the new row picks
    // up the just-created invitation (or the existing membership)
    // immediately instead of waiting for the 60s poll.
    workosRoster.refresh();
    toast({
      title: 'User added',
      sub: outcome.note,
      kind: outcome.kind === 'invite-failed' ? 'warn' : 'good',
    });
  }

  /**
   * "Resend invite" row action (WORKOS-INVITE-FLOW-PLAN Phase 4) — re-fires
   * the same `/api/invite-user` Function the Add User flow uses, for pending
   * rows whose original email never arrived or expired. WorkOS treats an
   * already-member as "skipped", so re-sending is always safe.
   */
  async function handleResendInvite(user: UserRow) {
    if (!inviteEnabled) {
      toast({
        title: 'Invites only fire on the deployed portal',
        sub: 'Set VITE_ENABLE_LOCAL_INVITES=true and run `func start` in api/ to test locally.',
        kind: 'info',
      });
      return;
    }
    if (resendingEmail !== null) return;
    setResendingEmail(user.email);
    try {
      const invite = await sendWorkosInvite({
        email: user.email,
        organizationId,
        getAccessToken,
        dev: import.meta.env.DEV,
      });
      // Refresh the WorkOS roster so the row's StatusPill flips to
      // "Invite sent" (with the new expiry) without waiting for the
      // 60s poll. Same rationale as the Add User flow.
      workosRoster.refresh();
      toast({
        title: invite.ok ? 'Invite re-sent' : 'Invite resend failed',
        sub: invite.sub,
        kind: invite.ok ? 'good' : 'warn',
      });
    } finally {
      setResendingEmail(null);
    }
  }

  async function handleAllowAuthoring(user: UserRow, allow: boolean) {
    const email = user.email.trim().toLowerCase();
    const ok = await runReducer('Allow authoring', async (conn) => {
      await conn.reducers.setSiteAuthoring({ email, projectId: defaultProjectId, allow });
      return true;
    });
    if (ok) {
      toast({
        title: allow ? 'Authoring allowed' : 'Authoring removed',
        sub: `${user.email} on ${defaultProjectId}`,
        kind: 'good',
      });
    }
  }

  async function handleOrientationAdmin(user: UserRow, on: boolean) {
    if (
      !(await dialogs.confirm({
        title: on
          ? `Make ${user.email} an orientation admin?`
          : `Remove orientation admin from ${user.email}?`,
        body: on
          ? 'They can build orientations and manage people, assignments and cohorts on every site, but not change site roles or invite. Applies when they sign in again.'
          : 'They keep only their site roles. Applies when they sign in again; sign them out in WorkOS if it must apply now.',
        confirmLabel: on ? 'Make orientation admin' : 'Remove',
      }))
    ) {
      return;
    }
    const res = await setWorkosMemberRole({
      email: user.email,
      organizationId,
      roleSlug: on ? 'orientation-admin' : 'member',
      getAccessToken,
    });
    if (!res.ok) {
      toast({ title: 'Org role not changed', sub: res.error, kind: 'bad' });
      return;
    }
    setOrientationAdminSet((m) => new Map(m).set(user.email, on));
    toast({
      title: on ? 'Orientation admin set' : 'Orientation admin removed',
      sub: `${user.email}: applies when they sign in again`,
      kind: 'good',
    });
  }

  async function handleSetOccupation(user: UserRow, occupation: string) {
    // Someone who has not signed in has only a pending staging row, and the
    // module can only SEED that row's occupation (upsert_pending_user fills an
    // empty one; add_user and set_pending_occupation both go through it), so
    // there is no way to change it from here yet. Say so plainly: the old
    // advice to "re-add them" did nothing either.
    if (!user.userId) {
      toast({
        title: 'Occupation can be changed after they sign in',
        sub: `${user.email} has not signed in yet. Their occupation is fixed until then; change it here once they have.`,
        kind: 'warn',
      });
      return;
    }
    const userId = user.userId;
    // Awaited, so the toast reports what the server did (was fire-and-forget).
    const ok = await runReducer('Set occupation', async (conn) => {
      await conn.reducers.setUserOccupation({ userId, occupation });
      return true;
    });
    if (!ok) return;
    toast({ title: 'Occupation updated', sub: `${user.email} → ${occupation || '(cleared)'}`, kind: 'good' });
    setModal({ kind: 'none' });
  }

  async function handleArchive(user: UserRow) {
    if (!user.userId) {
      toast({
        title: 'Cannot archive someone who has not signed in',
        sub: 'Archiving and deleting work once they have signed in. Until then their invite stays pending.',
        kind: 'warn',
      });
      return;
    }
    if (
      !(await dialogs.confirm({
        title: `Archive ${user.email}?`,
        body: 'Every site membership is soft-detached (kept for audit).',
        confirmLabel: 'Archive',
      }))
    ) {
      return;
    }
    void runReducer('Archive user', (conn) => {
      conn.reducers.archiveUser({ userId: user.userId! });
      toast({ title: 'User archived', kind: 'good' });
    });
  }

  async function handleDelete(user: UserRow) {
    if (!user.userId) {
      toast({
        title: 'Cannot delete someone who has not signed in',
        sub: 'Archiving and deleting work once they have signed in. Until then their invite stays pending.',
        kind: 'warn',
      });
      return;
    }
    if (
      !(await dialogs.confirm({
        title: `Permanently delete ${user.email}?`,
        body: 'This hard-deletes the user and every site membership. The module refuses if they have non-cancelled assignments — archive instead in that case. Cannot be undone.',
        confirmLabel: 'Delete permanently',
        danger: true,
      }))
    ) {
      return;
    }
    void runReducer('Delete user', (conn) => {
      conn.reducers.deleteUser({ userId: user.userId! });
      toast({
        title: 'Delete request sent',
        sub: `${user.email} — watch for module rejection if there are active assignments`,
        kind: 'good',
      });
    });
  }

  useEffect(() => {
    function handler(ev: PromiseRejectionEvent) {
      console.error('[workforce] unhandled rejection', ev.reason);
    }
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);

  if (sitesReady && sites.length === 0) return <NoProjects pageName="Workforce" />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 style={{ margin: 0, fontSize: 18 }}>Workforce</h1>
          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>
            {visibleUsers.length} of {users.length}
            {currentSite ? <> · {currentSite.name}</> : null}
            {' · '}{boundCount} signed-in · {pendingCount} pending
            {connOnline ? null : <span style={{ color: 'var(--bad)' }}> · offline</span>}
          </div>
        </div>
        {access.manageRoles ? (
        <div className="page-actions" style={{ display: 'flex', gap: 6 }}>
          <GatedButton
            className="btn sm primary"
            busy={busy !== null}
            reason={connOnline ? null : 'Reconnecting to MineTwin…'}
            onClick={() => setModal({ kind: 'add' })}
          >
            + Add user
          </GatedButton>
          <GatedButton
            className="btn sm"
            busy={busy !== null}
            reason={connOnline ? null : 'Reconnecting to MineTwin…'}
            hint="Paste a list of emails — one line each, with an optional occupation"
            onClick={() => setModal({ kind: 'bulk' })}
          >
            Add several
          </GatedButton>
        </div>
        ) : null}
      </div>

      <div className="page-body">
        {membership.isReady && currentSite && !membership.isMember ? (
          <div
            className="card"
            style={{
              padding: '12px 16px',
              marginBottom: 10,
              borderLeft: '3px solid var(--warn)',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              You are not a member of {currentSite.name} on this console yet
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
              The roster below shows only what you are allowed to see —
              likely nothing. Workforce stays open so this can be fixed:
              use <b>+ Add user</b> with your own email (org admins can do
              this directly; otherwise ask a site admin to add you).
            </div>
          </div>
        ) : null}
        <div className="card">
          <div className="card-head" style={{ padding: '10px 12px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 13 }}>Roster</h3>
              <div className="sub" style={{ fontSize: 11.5 }}>
                {currentSite
                  ? <>Attached to <b>{currentSite.name}</b>.</>
                  : 'Every site you can see.'}
              </div>
            </div>
          </div>

          {!isReady ? (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                color: 'var(--muted)',
                fontSize: 12.5,
              }}
            >
              Loading roster…
            </div>
          ) : visibleUsers.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: 'center',
                color: 'var(--muted)',
                fontSize: 12.5,
              }}
            >
              {users.length === 0 ? (
                <>
                  No users on this site yet. Use <b>Add user</b> to
                  create the first one.
                </>
              ) : currentSite ? (
                <>
                  No users on <b>{currentSite.name}</b> yet. There are{' '}
                  {users.length} on other sites — switch sites in the
                  topbar to see them.
                </>
              ) : (
                <>No users match the current filter.</>
              )}
            </div>
          ) : (
            <>
              {/* Smart-table toolbar: search + status chips + tier picker.
                  Sits between the card-head and the table itself; updates
                  filteredAndSortedUsers reactively. */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  alignItems: 'center',
                  padding: '10px 12px',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <input
                  className="inp"
                  type="search"
                  placeholder="Search email, name, occupation…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ flex: 1, minWidth: 180, maxWidth: 360, fontSize: 12 }}
                />
                <div style={{ display: 'flex', gap: 4 }}>
                  {(
                    [
                      { v: 'all' as const, label: 'All' },
                      { v: 'bound' as const, label: 'Signed-in' },
                      { v: 'pending' as const, label: 'Pending' },
                    ]
                  ).map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      className={
                        'chip-filter ' + (statusFilter === opt.v ? 'on' : '')
                      }
                      onClick={() => setStatusFilter(opt.v)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <select
                  className="inp"
                  value={tierFilter}
                  onChange={(e) => setTierFilter(e.target.value)}
                  style={{ maxWidth: 130, fontSize: 12 }}
                  aria-label="Filter by tier"
                >
                  <option value="all">All tiers</option>
                  <option value="OrgAdmin">Org admin</option>
                  <option value="OrientationAdmin">Orientation admin</option>
                  <option value="Admin">Site admin (legacy)</option>
                  <option value="OrgViewer">Org viewer</option>
                  <option value="Member">Member</option>
                  <option value="Contractor">Contractor</option>
                </select>
                <div
                  style={{
                    marginLeft: 'auto',
                    fontSize: 11,
                    color: 'var(--muted)',
                  }}
                >
                  {filteredAndSortedUsers.length} of {visibleUsers.length}
                </div>
              </div>
              {filteredAndSortedUsers.length === 0 ? (
                <div
                  style={{
                    padding: 24,
                    textAlign: 'center',
                    color: 'var(--muted)',
                    fontSize: 12.5,
                  }}
                >
                  No users match the current filter. Clear the search or
                  switch filter chips.
                </div>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <SortableTh
                        col="email"
                        label="Email"
                        sortBy={sortBy}
                        onClick={toggleSort}
                      />
                      <SortableTh
                        col="occupation"
                        label="Occupation"
                        sortBy={sortBy}
                        onClick={toggleSort}
                      />
                      <SortableTh
                        col="tier"
                        label="Tier"
                        sortBy={sortBy}
                        onClick={toggleSort}
                      />
                      <th>Rights</th>
                      <SortableTh
                        col="status"
                        label="Status"
                        sortBy={sortBy}
                        onClick={toggleSort}
                      />
                      <th aria-label="Actions" style={{ width: 48 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedUsers.map((u) => (
                  <tr key={u.userId ?? `pending:${u.email}`}>
                    <td>
                      <b>{u.email}</b>
                      {u.displayName !== u.email ? (
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                          {u.displayName}
                        </div>
                      ) : null}
                    </td>
                    <td>
                      {u.occupation || (
                        <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
                          —
                        </span>
                      )}
                    </td>
                    <td>
                      <TierPill role={u.appRole} bound={u.bound} />
                    </td>
                    <td>
                      <RightsCell
                        right={rightsByEmail.get(u.email.trim().toLowerCase())}
                        showAuthoring={showAllowAuthoring(
                          rightsByEmail.get(u.email.trim().toLowerCase())?.role ?? '',
                          access.siteAdmin,
                        )}
                        showOrientationAdmin={showOrientationAdminSwitch(viewerIsWorkosAdmin, u.appRole?.tag)}
                        orientationAdmin={
                          orientationAdminSet.get(u.email) ?? u.appRole?.tag === 'OrientationAdmin'
                        }
                        disabled={busy !== null}
                        onAllowAuthoring={(allow) => void handleAllowAuthoring(u, allow)}
                        onOrientationAdmin={(on) => void handleOrientationAdmin(u, on)}
                      />
                    </td>
                    <td>
                      <StatusPill
                        bound={u.bound}
                        email={u.email}
                        workos={workosRoster}
                      />
                    </td>
                    <td>
                      <RowActionsMenu
                        disabled={busy !== null}
                        actions={[
                          // Pending rows lead with the invite rescue — the
                          // only action that actually moves a pending user
                          // forward (Phase 4 of the invite plan).
                          ...(!u.bound
                            ? [
                                {
                                  label:
                                    resendingEmail === u.email
                                      ? 'Resending…'
                                      : 'Resend invite',
                                  onClick: () => void handleResendInvite(u),
                                  hint: 'Re-fires the WorkOS invitation email — use when the original never arrived or expired.',
                                },
                              ]
                            : []),
                          {
                            label: 'Set occupation',
                            onClick: () =>
                              setModal({ kind: 'occupation', user: u }),
                            hint: 'Mine job — drives which orientation gets assigned.',
                          },
                          {
                            label: 'Archive',
                            onClick: () => handleArchive(u),
                          },
                          {
                            label: 'Delete',
                            danger: true,
                            onClick: () => handleDelete(u),
                            hint: 'Hard delete (cascades site memberships). Blocked if active assignments exist.',
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>

      {modal.kind === 'add' && (
        <AddUserModal
          defaultProjectId={defaultProjectId}
          busy={busy !== null || adding}
          onCancel={() => setModal({ kind: 'none' })}
          onSubmit={handleAdd}
          onSwitchToBulk={() => setModal({ kind: 'bulk' })}
          viewerIsOrgAdmin={viewerIsOrgAdmin}
          viewerIsSiteAdmin={access.siteAdmin}
          users={users}
          workosRoster={workosRoster}
        />
      )}
      {modal.kind === 'bulk' && (
        <BulkAddUsersModal
          projectId={defaultProjectId}
          siteName={currentSite?.name ?? defaultProjectId}
          users={users}
          viewerIsOrgAdmin={viewerIsOrgAdmin}
          onCancel={() => setModal({ kind: 'none' })}
          addOne={addOneUser}
          onFinished={(summary) => {
            workosRoster.refresh();
            toast(summary);
          }}
        />
      )}
      {modal.kind === 'occupation' && (
        <SetOccupationModal
          user={modal.user}
          busy={busy !== null}
          onCancel={() => setModal({ kind: 'none' })}
          onSubmit={(occ) => handleSetOccupation(modal.user, occ)}
        />
      )}
    </>
  );
}

/** Clickable column header. Click toggles sort direction; clicking a
 *  different column resets to ascending. Active column gets a triangle
 *  indicator. */
function SortableTh({
  col,
  label,
  sortBy,
  onClick,
}: {
  col: 'email' | 'occupation' | 'tier' | 'status';
  label: string;
  sortBy: { col: 'email' | 'occupation' | 'tier' | 'status'; dir: 'asc' | 'desc' };
  onClick: (col: 'email' | 'occupation' | 'tier' | 'status') => void;
}) {
  const active = sortBy.col === col;
  const ariaSort: 'ascending' | 'descending' | 'none' = active
    ? sortBy.dir === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';
  return (
    <th
      onClick={() => onClick(col)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      aria-sort={ariaSort}
    >
      {label}
      {active && (
        <span style={{ color: 'var(--muted)', display: 'inline-flex', verticalAlign: '-1px', marginLeft: 3 }}>
          <Icon of={sortBy.dir === 'asc' ? PortalIcons.action.collapse : PortalIcons.action.expand} size={10} />
        </span>
      )}
    </th>
  );
}

/**
 * Combines MineTwin "has the user connected to the module?" state
 * (`bound`) with WorkOS-side identity state (`workos`) into a single
 * roster badge. Replaces the bare "Signed in / Pending" pill so the
 * coordinator can immediately tell whether a "pending" row is an
 * already-known WorkOS member who just hasn't opened the portal yet
 * vs. a fresh invite still in flight vs. neither (which means
 * `add_user` ran but the WorkOS invite step failed silently).
 *
 * Four states:
 *   bound + member            → "Active"
 *   pending + member          → "WorkOS member · not signed in"
 *   pending + invited         → "Invite sent" (tooltip carries expiry)
 *   pending + neither         → "Awaiting invite" (red — shouldn't
 *                                happen after a successful Add User)
 *
 * If the WorkOS roster fetch hasn't resolved yet OR failed (e.g.
 * `func start` not running in dev), the pill falls back to the bare
 * "Signed in / Pending" labels so the table still reads correctly.
 */
function StatusPill({
  bound,
  email,
  workos,
}: {
  bound: boolean;
  email: string;
  workos: WorkosRosterState;
}) {
  const lowerEmail = email.trim().toLowerCase();
  const isMember = workos.memberEmails.has(lowerEmail);
  const invite = workos.pendingInvitesByEmail.get(lowerEmail);
  // Render shape: bg, fg, label, tooltip.
  let bg = 'var(--panel-3)';
  let fg = 'var(--muted)';
  let label = bound ? 'Signed in' : 'Pending';
  let tooltip: string | undefined;

  if (workos.isReady && !workos.error) {
    if (bound && isMember) {
      bg = 'var(--good-soft)';
      fg = 'var(--good)';
      label = 'Active';
      tooltip = 'Signed into the portal · WorkOS org member.';
    } else if (bound) {
      // Edge case: signed-in MineTwin user whose WorkOS membership
      // was revoked. Show "Active" but flag.
      bg = 'var(--warn-soft)';
      fg = 'var(--warn)';
      label = 'Signed in · WorkOS unknown';
      tooltip =
        'Signed in to MineTwin but WorkOS does not list them as an org member. Their WorkOS membership may have been revoked.';
    } else if (isMember) {
      bg = 'var(--good-soft)';
      fg = 'var(--good)';
      label = 'WorkOS member · not signed in';
      tooltip =
        'Active WorkOS org member who has not yet opened the portal. They will appear as "Active" after their first sign-in.';
    } else if (invite) {
      bg = 'var(--warn-soft)';
      fg = 'var(--warn)';
      label = 'Invite sent';
      tooltip = invite.expiresAt
        ? `WorkOS invitation pending · expires ${new Date(invite.expiresAt).toLocaleDateString()}`
        : 'WorkOS invitation pending acceptance.';
    } else {
      bg = 'var(--bad-soft, rgba(232,92,92,0.18))';
      fg = 'var(--bad)';
      label = 'Awaiting invite';
      tooltip =
        'Not in WorkOS yet and no outstanding invitation. The WorkOS invite step may have failed during Add User — try re-adding or invite from the WorkOS dashboard.';
    }
  } else if (workos.isReady && workos.error) {
    // Show the bare MineTwin state but signal in the tooltip that
    // WorkOS state is unknown.
    bg = bound ? 'var(--good-soft)' : 'var(--panel-3)';
    fg = bound ? 'var(--good)' : 'var(--muted)';
    label = bound ? 'Signed in' : 'Pending';
    tooltip = `WorkOS state unavailable: ${workos.error}`;
  }
  return (
    <span
      title={tooltip}
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 10,
        background: bg,
        color: fg,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
}

function TierPill({
  role,
  bound,
}: {
  role: UserRow['appRole'];
  bound: boolean;
}) {
  if (!bound || !role) {
    return (
      <span style={{ color: 'var(--muted)', fontStyle: 'italic', fontSize: 11 }}>
        —
      </span>
    );
  }
  const styles: Record<
    string,
    { bg: string; fg: string; label: string }
  > = {
    OrgAdmin: { bg: 'var(--good-soft)', fg: 'var(--good)', label: 'Org admin' },
    OrgViewer: { bg: 'var(--panel-3)', fg: 'var(--muted)', label: 'Org viewer' },
    OrientationAdmin: { bg: 'var(--good-soft)', fg: 'var(--good)', label: 'Orientation admin' },
    Admin: { bg: 'var(--warn-soft)', fg: 'var(--warn)', label: 'Site admin (legacy)' },
    Member: { bg: 'var(--panel-3)', fg: 'var(--muted)', label: 'Member' },
    Contractor: {
      bg: 'var(--panel-3)',
      fg: 'var(--muted)',
      label: 'Contractor',
    },
  };
  const s = styles[role.tag] ?? styles.Member;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 10,
        background: s.bg,
        color: s.fg,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {s.label}
    </span>
  );
}

/**
 * The site role from `visible_site_rights`, the Allow authoring switch on a
 * Reader for a site admin (P1), and the Orientation admin switch for a
 * WorkOS admin (P3).
 */
function RightsCell({
  right,
  showAuthoring,
  showOrientationAdmin,
  orientationAdmin,
  disabled,
  onAllowAuthoring,
  onOrientationAdmin,
}: {
  right: { role: string; allowAuthoring: boolean } | undefined;
  showAuthoring: boolean;
  showOrientationAdmin: boolean;
  orientationAdmin: boolean;
  disabled: boolean;
  onAllowAuthoring: (allow: boolean) => void;
  onOrientationAdmin: (on: boolean) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11.5 }}>
      <span style={{ color: right?.role ? undefined : 'var(--muted)' }}>{right?.role || '-'}</span>
      {showAuthoring ? (
        <label title={ALLOW_AUTHORING_HINT} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input
            type="checkbox"
            role="switch"
            checked={!!right?.allowAuthoring}
            disabled={disabled}
            onChange={(e) => onAllowAuthoring(e.target.checked)}
          />
          Allow authoring
        </label>
      ) : null}
      {showOrientationAdmin ? (
        <label
          title="Org-wide: builds orientations and manages people on every site. Applies when they sign in again."
          style={{ display: 'flex', gap: 4, alignItems: 'center' }}
        >
          <input
            type="checkbox"
            role="switch"
            checked={orientationAdmin}
            disabled={disabled}
            onChange={(e) => onOrientationAdmin(e.target.checked)}
          />
          Orientation admin
        </label>
      ) : null}
    </div>
  );
}

type RowAction = {
  label: string;
  onClick: () => void;
  /** Renders the item in a destructive red style. */
  danger?: boolean;
  /** Optional tooltip / inline hint shown under the label. */
  hint?: string;
};

function RowActionsMenu({
  actions,
  disabled,
}: {
  actions: RowAction[];
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(
    null,
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Click outside the wrapper AND the floating menu closes it. With
  // position: fixed the menu lives outside the wrapper subtree, so the
  // generic useOutsideClick (which checks only the ref) would treat a
  // click on a menu item as "outside" and snap shut before the item's
  // onClick fires.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (wrapperRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Compute fixed-positioned menu coordinates from the kebab button's
  // viewport rect at open time, and flip upward if there isn't enough
  // space below. position: fixed escapes any clipping container (the
  // card / table-wrapper used to cut off the popover for the last row).
  const MENU_HEIGHT_ESTIMATE = 160;
  function handleOpen() {
    if (disabled) return;
    const next = !open;
    if (next && wrapperRef.current) {
      const r = wrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const flipUp = spaceBelow < MENU_HEIGHT_ESTIMATE && r.top > MENU_HEIGHT_ESTIMATE;
      setMenuPos({
        top: flipUp ? r.top - 4 - MENU_HEIGHT_ESTIMATE : r.bottom + 4,
        right: window.innerWidth - r.right,
      });
    }
    setOpen(next);
  }

  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <button
        type="button"
        className="icon-btn"
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : 'false'}
        aria-label="Row actions"
        disabled={disabled}
        onClick={handleOpen}
        style={{
          width: 26,
          height: 26,
          padding: 0,
          lineHeight: 1,
          fontSize: 16,
          fontWeight: 700,
          color: 'var(--muted)',
        }}
      >
        <Icon of={Icons.action.more} size={16} />
      </button>
      {open && menuPos && (
        <div
          role="menu"
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuPos.top,
            right: menuPos.right,
            minWidth: 180,
            // Theme-aware surface: `--panel` is white in light theme,
            // near-black in dark. Was `var(--panel-1, #0E1622)` — an
            // undefined var falling back to a hardcoded dark color — plus
            // hardcoded near-white text, which rendered as a dark box in
            // light theme. Matches the EmailSuggest / Occupation dropdowns.
            background: 'var(--panel)',
            border: '1px solid var(--border-2)',
            borderRadius: 'var(--r, 6px)',
            padding: 4,
            zIndex: 1000,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            color: 'var(--text)',
            fontSize: 12.5,
          }}
        >
          {actions.map((a) => (
            <button
              key={a.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                a.onClick();
              }}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '6px 10px',
                background: 'transparent',
                border: 0,
                borderRadius: 4,
                cursor: 'pointer',
                color: a.danger ? 'var(--bad, #ef6868)' : 'inherit',
                font: 'inherit',
              }}
              onMouseEnter={(ev) => {
                (ev.currentTarget as HTMLButtonElement).style.background =
                  'var(--panel-2)';
              }}
              onMouseLeave={(ev) => {
                (ev.currentTarget as HTMLButtonElement).style.background =
                  'transparent';
              }}
            >
              <div style={{ fontWeight: 600 }}>{a.label}</div>
              {a.hint ? (
                <div
                  style={{
                    fontSize: 10.5,
                    color: 'var(--muted)',
                    marginTop: 2,
                    fontWeight: 400,
                  }}
                >
                  {a.hint}
                </div>
              ) : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ----- Modals -------------------------------------------------------------

function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        fontSize: 12,
      }}
    >
      <span className="label-up">
        {label}
        {required ? ' *' : ''}
      </span>
      <input
        className="inp"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

// OccupationSelect moved to src/components/OccupationSelect.tsx so Cohort
// Builder and any future orientation-author surface can share it.

function SetOccupationModal({
  user,
  busy,
  onCancel,
  onSubmit,
}: {
  user: UserRow;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (occupation: string) => void;
}) {
  const [occupation, setOccupation] = useState(user.occupation);
  const changed = occupation.trim() !== user.occupation;
  const disabledReason = changed ? undefined : 'No change';

  return (
    <DrawerShell
      title={`Set occupation · ${user.email}`}
      busy={busy}
      canSubmit={changed}
      disabledReason={disabledReason}
      submitLabel="Save"
      onCancel={onCancel}
      onSubmit={() => onSubmit(occupation.trim())}
    >
      <OccupationSelect value={occupation} onChange={setOccupation} />
    </DrawerShell>
  );
}

